import React, { cloneElement, Fragment, isValidElement, lazy, Suspense, useEffect, useReducer } from 'react';
import { ModalType, Size, ThemeTonalRange } from '@philips-internal/oswald-ui-constants';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, deprecationWarningMessage, isContentfulString, isEmptyObject } from '@philips-internal/oswald-ui-utils';
import { useIsMobileResolution } from '../../hooks/use-is-mobile-resolution.js';
import { useRouterContext } from '../../hooks/use-router-context.js';
import { FieldOrientation } from '../field/field-constants.js';
import Field from '../field/field.js';
import { Form } from '../form/form.js';
import { InlineImage } from '../inline-image/inline-image.js';
import { Input } from '../input/input.js';
import { Link } from '../link/link.js';
import { Loader } from '../loader/index.js';
import { Modal } from '../modal/modal.js';
import { Text } from '../text/text.js';
import { AboutBoxProperties } from './about-box-types.js';
import './about-box.css';
const MANIFEST = '/manifest.json';
const UNSPECIFIED = 'tws.about-box.unspecified';
const Markdown = lazy(() => import('react-markdown'));
function AboutBoxBadge(props) {
    if (!isContentfulString(props.text)) {
        return null;
    }
    return (React.createElement("div", { className: "c-about-box__badge" },
        props.icon,
        React.createElement("span", { className: "c-about-box__badge-text u-hide@empty" }, MessageService.translate(props.text))));
}
function AboutBoxInformation(props) {
    if (!isContentfulString(props.text)) {
        return null;
    }
    const className = classNames({
        'c-about-box__information': true,
        [`${props.className}`]: Boolean(props.className),
    });
    return (React.createElement("div", { className: className },
        React.createElement("span", { className: "c-about-box__information-icon" }, props.icon),
        React.createElement(Text, null, MessageService.translate(props.text))));
}
function LocalizedLabelLink(props) {
    if (isEmptyObject(props.localizedLabel) || !isContentfulString(props.localizedLabel?.label)) {
        return null;
    }
    return (React.createElement("div", { className: "c-about-box__information" },
        React.createElement(Link, { bold: true, className: "c-about-box__localized-label-link", onClick: props.onClick, size: Size.LARGE, value: props.localizedLabel?.label })));
}
function AboutBoxIcon(props) {
    if (!props.icon) {
        return null;
    }
    const className = classNames({
        'c-about-box__icon': true,
        [`${props.className}`]: Boolean(props.className),
    });
    if (isValidElement(props.icon)) {
        const elementProps = {
            ...props.icon.props,
            className,
        };
        return cloneElement(props.icon, elementProps);
    }
    return React.createElement(InlineImage, { className: className, src: props.icon });
}
function LocalizedLabelModal(props) {
    const isMobileResolution = useIsMobileResolution();
    if (!props.localizedLabel || isEmptyObject(props.localizedLabel) || !isContentfulString(props.localizedLabel?.label)) {
        return null;
    }
    const localizedLabelModalData = props.localizedLabel.data.reduce((accumulator, current) => {
        accumulator.fields.push(React.createElement(Field, { entry: current.label, key: current.label, label: current.label, orientation: FieldOrientation.VERTICAL, uuid: "fake-uuid", width: 12 },
            React.createElement(Input, null)));
        if (!accumulator.formData[current.label]) {
            accumulator.formData[current.label] = current.value;
        }
        return accumulator;
    }, { fields: [], formData: {} });
    if (!localizedLabelModalData.formData || isEmptyObject(localizedLabelModalData.formData)) {
        return null;
    }
    return (React.createElement(Modal, { onClose: props.onClose, theme: ThemeTonalRange.BRIGHT, type: isMobileResolution ? ModalType.FULLSCREEN : ModalType.DEFAULT },
        React.createElement("div", { className: "c-about-box__modal-content" },
            React.createElement(AboutBoxIcon, { className: "c-about-box__logo", icon: props.logoIcon }),
            React.createElement(Form, { className: "c-about-box__localized-label-form", data: localizedLabelModalData.formData, locked: true, uuid: "fake-uuid" }, localizedLabelModalData.fields))));
}
const reducerInitialState = { localizedLabelModalOpen: false };
function reducer(data, partialData) {
    return {
        ...data,
        ...partialData,
    };
}
function AboutBox(props) {
    const [reducerState, setReducerState] = useReducer(reducer, reducerInitialState);
    const { cautionIcon = AboutBoxProperties.cautionIcon.defaultValue, companyAddress = AboutBoxProperties.companyAddress.defaultValue, companyAddressIcon = AboutBoxProperties.companyAddressIcon.defaultValue, companyAddressOutlineIcon = AboutBoxProperties.companyAddressOutlineIcon.defaultValue, companyComplementaryAddress = AboutBoxProperties.companyComplementaryAddress.defaultValue, companyReference = AboutBoxProperties.companyReference.defaultValue, companyReferenceIcon = AboutBoxProperties.companyReferenceIcon.defaultValue, logoIcon = AboutBoxProperties.logoIcon.defaultValue, lotNumberIcon = AboutBoxProperties.lotNumberIcon.defaultValue, messageExpression = AboutBoxProperties.messageExpression.defaultValue, productName = AboutBoxProperties.productName.defaultValue, productSubtitle = AboutBoxProperties.productSubtitle.defaultValue, quantityIcon = AboutBoxProperties.quantityIcon.defaultValue, referenceIcon = AboutBoxProperties.referenceIcon.defaultValue, secondaryMessageExpression = AboutBoxProperties.secondaryMessageExpression.defaultValue, showQuantity = AboutBoxProperties.showQuantity.defaultValue, uniqueDeviceIdentifierIcon = AboutBoxProperties.uniqueDeviceIdentifierIcon.defaultValue, versionExpression = AboutBoxProperties.versionExpression.defaultValue, } = props;
    const [href] = useRouterContext(MANIFEST);
    const hasComplementaryAddress = isContentfulString(companyComplementaryAddress)
        && MessageService.translate(companyComplementaryAddress) !== 'tws.about-box.company-complementary-address';
    useEffect(() => {
        if (!isContentfulString(href)) {
            return;
        }
        const headers = new Headers({
            'cache-control': 'no-cache',
            'pragma': 'no-cache',
        });
        fetch(href, { headers })
            .then((response) => response.ok ? response.json() : Promise.reject())
            .then((manifest) => setReducerState({ ...manifest }))
            .catch(() => setReducerState({ version: UNSPECIFIED }));
    }, [href]);
    useEffect(() => {
        if (!reducerState.versionDate) {
            return;
        }
        deprecationWarningMessage({
            deadlineVersion: '7.0.0',
            property: true,
            replaced: 'versionDate',
            scope: 'AboutBox',
            startingVersion: '6.13.1',
        });
    }, [reducerState.versionDate]);
    if (reducerState.localizedLabelModalOpen) {
        return (React.createElement(LocalizedLabelModal, { localizedLabel: props.localizedLabel, logoIcon: logoIcon, onClose: () => setReducerState({ localizedLabelModalOpen: false }) }));
    }
    const aboutMessage = MessageService.translate(messageExpression, { year: new Date().getFullYear() });
    return (React.createElement("div", { className: "c-about-box" },
        React.createElement("div", { className: "c-about-box__content" },
            React.createElement(AboutBoxIcon, { className: "c-about-box__logo", icon: logoIcon }),
            React.createElement("div", { className: "c-about-box__text c-about-box__name" }, MessageService.translate(productName)),
            React.createElement("div", { className: "c-about-box__text c-about-box__subtitle" }, MessageService.translate(productSubtitle)),
            React.createElement("div", { className: "c-about-box__text c-about-box__version" },
                React.createElement("span", { className: "c-about-box__version-number" },
                    MessageService.translate(versionExpression),
                    " ",
                    MessageService.translate(reducerState.version ?? ''))),
            React.createElement("div", { className: "c-about-box__badge-wrapper" },
                React.createElement(AboutBoxBadge, { icon: React.createElement(AboutBoxIcon, { icon: lotNumberIcon }), text: reducerState.LOT }),
                React.createElement(AboutBoxBadge, { icon: React.createElement(AboutBoxIcon, { icon: referenceIcon }), text: reducerState.REF }),
                showQuantity ? React.createElement(AboutBoxIcon, { className: "c-about-box__quantity-icon", icon: quantityIcon }) : null),
            React.createElement("div", { className: "c-about-box__text c-about-box__message c-about-box__markdown" },
                React.createElement(Suspense, { fallback: React.createElement(Loader, null) },
                    React.createElement(Markdown, null, aboutMessage),
                    React.createElement(Markdown, null, MessageService.translate(secondaryMessageExpression || '')))),
            React.createElement(AboutBoxInformation, { className: "c-about-box__manufacture-icon", icon: React.createElement(AboutBoxIcon, { icon: companyAddressIcon }), text: companyAddress }),
            hasComplementaryAddress && React.createElement(AboutBoxInformation, { text: companyComplementaryAddress }),
            React.createElement(AboutBoxInformation, { icon: React.createElement(AboutBoxIcon, { icon: companyReferenceIcon }), text: companyReference }),
            React.createElement(AboutBoxInformation, { icon: (React.createElement(Fragment, null,
                    React.createElement(AboutBoxIcon, { icon: cautionIcon }),
                    React.createElement(AboutBoxIcon, { icon: companyAddressOutlineIcon }),
                    props.customIcons?.map((item, index) => React.createElement(AboutBoxIcon, { icon: item, key: `about-box-custom-icon-${index}` })))), text: reducerState.date ?? reducerState.versionDate }),
            React.createElement(LocalizedLabelLink, { localizedLabel: props.localizedLabel, onClick: () => setReducerState({ localizedLabelModalOpen: true }) }),
            React.createElement(AboutBoxInformation, { icon: React.createElement(AboutBoxIcon, { icon: uniqueDeviceIdentifierIcon }), text: reducerState.UDI }))));
}
export { AboutBox, };
//# sourceMappingURL=about-box.js.map